import { AsyncPipe } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  HostListener,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { AuthenticationService } from '@auth/authentication.service';
import { Screens } from '@enums/role-permissions.enum';
import { UserRoles } from '@enums/user-roles.enum';
import { IonContent, IonMenu, MenuController } from '@ionic/angular/standalone';
import { CurrentLocation } from '@models/admin/location-admin.model';
import { LocationService } from '@services/admin/location/location.service';
import { UserService } from '@services/admin/user/user.service';
import { CommonUtils } from '@utils/common/common.utils';
import { ScreenPermissionsUtils } from '@utils/permissions/screen-permissions/screen-permissions.utils';
import {
  distinctUntilChanged,
  filter,
  from,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { DisplayOnlyIconComponent } from './display-only-icon/display-only-icon.component';
import { NavIconComponent } from './nav-icon/nav-icon.component';
import {
  CLOSED_SIDENAV_VALUES,
  OPEN_SIDENAV_VALUES,
  SideNavType,
} from './side-nav.constants';
import { SublinkIconComponent } from './sublink-icon/sublink-icon.component';

@Component({
  selector: 'app-side-nav',
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.scss'],
  imports: [
    DisplayOnlyIconComponent,
    NavIconComponent,
    SublinkIconComponent,
    AsyncPipe,
    IonMenu,
    IonContent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SideNavComponent implements OnInit {
  readonly destroy = inject(DestroyRef);
  commonUtils = inject(CommonUtils);
  roleUtils = inject(ScreenPermissionsUtils);
  authService = inject(AuthenticationService);
  router = inject(Router);
  private route: Router = inject(Router);
  private userService = inject(UserService);
  private locationService = inject(LocationService);
  readonly menuController = inject(MenuController);

  readonly CLOSED_VALUES = CLOSED_SIDENAV_VALUES;
  readonly OPEN_VALUES = OPEN_SIDENAV_VALUES;
  readonly SCREENS = Screens;
  readonly TYPE = SideNavType;

  currentLocation!: CurrentLocation | undefined;
  userRole!: UserRoles;
  allowToggle = false;
  sciIndicator = 0;

  ngOnInit(): void {
    //On route change close the sidebar
    this.route.events
      .pipe(
        takeUntilDestroyed(this.destroy),
        filter((event) => event instanceof NavigationStart),
        switchMap((event) => {
          if (event) {
            return from(this.menuController.close('menu'));
          } else {
            return of();
          }
        }),
      )
      .subscribe();

    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        map((user) => {
          this.sciIndicator = user.sciIndicator ?? 0;
          if (user?.roleName) {
            this.userRole = user.roleName;
          }
        }),
      )
      .subscribe();

    this.locationService
      .getCurrentLocation$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        tap((val) => {
          this.currentLocation = val;
        }),
      )
      .subscribe();

    this.authService.isDrawerOpen$
      .asObservable()
      .pipe(
        takeUntilDestroyed(this.destroy),
        distinctUntilChanged(),
        tap(() => {
          this.toggleDrawer();
        }),
      )
      .subscribe();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.commonUtils.updateBanner(false);
  }

  toggleDrawer(): void {
    // check allow flag and toggle only if not on login page, otherwise close the drawer
    if (!this.router.url.includes('/login')) {
      if (this.authService.allowDrawerToggle()) {
        this.menuController.toggle('menu');
      } else {
        this.menuController.close('menu');
      }
    }
  }
}
